<template>
  <div class="flex flex-col h-full conversations">
    <app-header class="z-10 pl-4">
      <filter-menu
        :show-request-outcomes="true"
        :show-assistance-resolutions="true"
        :show-answer="true"
        :show-channels="true"
        :is-loading="loaders.filters"
        @filter-change="onFilterChanged"
      />
    </app-header>

    <empty-view v-if="!hasConversations && !initialLoading"
                text="No conversations match your selected filters."
                :is-loading="loaders.conversations"
                image-name="noConversationsImage"
                class="h-full justify-center items-center test-empty-view-conversations"/>

    <div v-else class="w-full h-full flex overflow-auto">
      <conversations-list ref="conversationList"
                          :conversations="conversations"
                          :has-next="hasNext"
                          :is-loading="loaders.conversations"
                          :contact-id="contactId"
                          :was-navigation-clicked="wasNavigationClicked()"
                          @select-conversation="selectConversation"
                          @load-conversations="loadMoreConversations(true)"
                          class="w-3/12 max-h-full"
      />
      <shadowed-wrapper class="w-5/12 flex flex-col h-full">
        <template>
          <conversation-detail :contact-id="contactId"
                               :selected-bubble="selectedBubble"
                               ref="conversationDetail"
                               class="h-full"
                               :is-loading.sync="loaders.conversationDetail"
                               :answers-by-intent-id="answersByIntentId"
                               :answers-by-id="answersById"
                               @client-message-selected="onClientMessageSelected"
                               @remote-action-selected="onRemoteActionSelected"
                               @chat-gpt-action-selected="onChatGPTActionSelected"
                               @event-selected="onEventSelected"
                               @clear-bubble-selected="onClearBubble"
          />
          <conversation-navigation ref="conversationNavigation"
                                   class="h-16"
                                   :contact-id="contactId"
                                   :is-conversation-loading="loaders.conversationDetail"
                                   :conversations="conversations"
                                   @bubble-selected="onClientMessageSelected"
                                   @select-conversation="selectConversation"
          />
        </template>
      </shadowed-wrapper>

      <transition name="slide-fade" mode="out-in">
        <empty-view v-if="!hasRequest"
                    text="There are no requests in the selected conversation."
                    class="w-4/12 my-auto"
                    image-name="noConversationsImage"
        />
        <answer-suggestion-form v-else-if="formData.intentSuggestion"
                                class="w-4/12"
                                :form-data="formData"
                                :conversations="conversations"
                                :contact-id="contactId"
                                :is-loading="loaders.conversationDetail"
                                :answers-by-intent-id="answersByIntentId"
                                @change-suggested-answer="changeSuggestedAnswer"
                                @answer-suggested="onAnswerSuggested"
                                @select-conversation="selectConversation"
                                @bubble-selected="onClientMessageSelected"
                                :key="formData.id"
        />
        <ChatGPTActionDetail v-else-if="formData.type === 'chat_gpt_action'"
                         class="w-4/12"
                         :form-data="formData"
                         :key="formData.id"
        />
        <EventDetail
          v-else-if="formData.type === 'event' || formData.type === 'bot_wake_up'"
          class="w-4/12"
          :form-data="formData"
          :key="formData.id"
        />
        <api-call-detail v-else
                         class="w-4/12"
                         :request-data="selectedBubble.requestData"
                         :response-data="selectedBubble.responseData"
                         :form-data="formData"
                         :key="formData.id"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import AppHeader from '@/components/common/AppHeader.vue'
import ConversationsList from './ConversationsList.vue'
import FilterMenu from '../filter/FilterMenu.vue'
import { FILTER_ITEMS, IGNORED_OUTCOMES } from '@/utils/filter.constants'
import clone from 'ramda/src/clone'
import isEmpty from 'ramda/src/isEmpty'
import analyticsService from '@/services/amio/analytics.service'
import ConversationDetail from './detail/ConversationDetail.vue'
import AnswerSuggestionForm from './detail/AnswerSuggestionForm.vue'
import EmptyView from '@/components/editor/EmptyView.vue'
import toaster from '@/utils/toaster'
import botModule from '@/store/modules/bot.module'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper.vue'
import { ROUTE_NAMES } from '@/router'
import ConversationNavigation from '@/components/analytics/conversations/detail/ConversationNavigation.vue'
import answerModule from '@/store/modules/answer.module'
import answerService from '@/services/amio/answer.service'
import channelModule from '@/store/modules/channel.module'
import errorUtils from '@/utils/error.utils'
import variablesModule from '@/store/modules/variables.module'
import ApiCallDetail from '@/components/analytics/conversations/detail/ApiCallDetail.vue'
import ChatGPTActionDetail from '@/components/analytics/conversations/detail/ChatGPTActionDetail.vue'
import EventDetail from '@/components/analytics/conversations/detail/EventDetail.vue'

export default {
  name: 'Conversations',

  metaInfo: {
    title: 'History'
  },

  components: {
    ApiCallDetail,
    ChatGPTActionDetail,
    EventDetail,
    ShadowedWrapper,
    AppHeader,
    ConversationsList,
    FilterMenu,
    ConversationDetail,
    EmptyView,
    AnswerSuggestionForm,
    ConversationNavigation
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      initialLoading: true,
      conversations: [],
      cursor: {},
      formData: {},
      selectedBubble: null,
      contactId: this.$route.query.contactId,
      loaders: {
        conversations: false,
        filters: true,
        conversationDetail: false
      }
    }
  },

  computed: {
    dateFrom: analyticsFilterModule.getDateFrom,
    dateTo: analyticsFilterModule.getDateTo,
    requestOutcomes: analyticsFilterModule.getRequestOutcomes,
    assistanceResolutions: analyticsFilterModule.getAssistanceResolutions,
    bot: analyticsFilterModule.getBot,
    answerId: analyticsFilterModule.getAnswerId,
    answerList: answerModule.getAnswerList,
    channels: analyticsFilterModule.getChannels,

    hasConversations() {
      return !isEmpty(this.conversations)
    },

    hasNext() {
      return isEmpty(this.cursor) ? false : this.cursor.has_next
    },

    hasRequest() {
      return !isEmpty(this.formData)
    },

    isContactIdLoaded() {
      return this.conversations.some(({ contact_id }) => contact_id === this.$route.query.contactId)
    },

    answersByIntentId() {
      const pairs = this.answerList.map(({ intent, name, id }) => {
        return [intent.platformId, { name, id, intentId: intent.id }]
      })
      return Object.fromEntries(pairs)
    },

    answersById() {
      const pairs = this.answerList.map(({ name, id }) => [id, { name, id }])
      return Object.fromEntries(pairs)
    }
  },

  methods: {
    wasNavigationClicked() {
      const { stepForwardWasClicked, stepBackWasClicked } = this.$refs.conversationNavigation || {}
      return stepForwardWasClicked || stepBackWasClicked
    },

    async loadInitialConversations() {
      this.setLoader('conversations', true)
      this.setConversations(await this.loadConversations(), true)

      if (!isEmpty(this.conversations)) {
        const isConversationExisting = await this.isConversationsExisting(this.$route.query.contactId)
        while (!this.isContactIdLoaded && isConversationExisting) {
          if (this.hasNext) {
            await this.loadMoreConversations(false)
            continue
          }
          break
        }

        const contactId = this.isContactIdLoaded ? this.contactId : this.conversations[0].contact_id
        this.selectConversation(contactId, true)
      }

      this.setLoader('conversations', false)
    },

    async loadConversations() {
      try {
        const [dataAutomations, dataResolutions] = await Promise.all(this.resolveConversationRequests())
        this.cursor = dataAutomations.cursor
        return this.getConversations(dataAutomations.conversations, dataResolutions?.conversations)
      } catch (err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription, err)
      }
    },

    async loadMoreConversations(isUserScrolling) {
      if (this.loaders.conversations && isUserScrolling) return

      this.setLoader('conversations', true)
      this.setConversations(await this.loadConversations())
      this.setLoader('conversations', false)
    },

    getConversations(conversationsAutomation, conversationsResolution) {
      if (!conversationsResolution) return conversationsAutomation

      const resolutionContactIds = new Set(conversationsResolution.map(({ contact_id }) => contact_id))

      return conversationsAutomation.filter(({ contact_id }) => {
        return resolutionContactIds.has(contact_id)
      })
    },

    setConversations(conversations, resetConversations = false) {
      if (resetConversations) {
        this.conversations = conversations
        return
      }

      this.conversations.push(...conversations)
    },

    resolveConversationRequests() {
      const payloadOutcomes = {
        botId: this.bot.id,
        from: this.dateFrom,
        to: this.dateTo,
        answerId: this.answerId,
        cursor: this.cursor,
        ignoreOutcomes: IGNORED_OUTCOMES,
        resolutions: null,
        outcomes: this.requestOutcomes,
        channels: this.channels
      }
      const requests = [analyticsService.getConversations(payloadOutcomes)]

      if (this.assistanceResolutions) {
        const payloadResolutions = {
          ...payloadOutcomes,
          outcomes: null,
          resolutions: this.assistanceResolutions,
          ignoreOutcomes: null
        }
        requests.push(analyticsService.getConversations(payloadResolutions))
      }

      return requests
    },

    async isConversationsExisting(contactId) {
      if (!contactId) return false

      try {
        const response = await analyticsService.getConversationHistory(contactId)
        return !isEmpty(response.history_events)
      } catch(err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription, err)
        // bootstrap conversations has no request and return error (500)
        return true
      }
    },

    async loadAnswers() {
      answerModule.setAnswerList(await answerService.list(this.botId))
    },

    async retrieveChannelList(botId) {
      analyticsFilterModule.setChannels(null)
      await this.initFilters(botId)
    },

    onClientMessageSelected(formData) {
      this.onClearBubble()
      this.formData = formData
      if (isEmpty(formData)) return

      this.selectedBubble = {
        messageId: formData.messageId,
        suggestedIntentPlatformId: formData.intentSuggestion.suggestedIntentPlatformId
      }

      if (formData.goToTargetMessage) {
        this.$refs.conversationDetail.handleScrollToBubble({ id: formData.messageId })
      }

      this.updateRouteQueryRequestId(formData.messageId)
    },

    updateRouteQueryRequestId(requestId) {
      if (this.$route.query.requestId === requestId) return

      this.$router.push(this.getMessageRoute(requestId))
    },

    getMessageRoute(requestId) {
      return {
        name: ROUTE_NAMES.history,
        params: this.$route.params,
        query: { ...this.$route.query, requestId, messageId: undefined }
      }
    },

    onAnswerSuggested() {
      this.selectedBubble = {
        messageId: this.formData.messageId,
        suggestedIntentPlatformId: this.formData.intentSuggestion.suggestedIntentPlatformId
      }
    },

    async onFilterChanged(filterValues) {
      this.setLoader('conversations', true)
      this.setLoader('conversationDetail', true)
      await analyticsFilterModule.setBot(filterValues[FILTER_ITEMS.BOT].id)
      analyticsFilterModule.setDateFrom(filterValues[FILTER_ITEMS.DATE_FROM])
      analyticsFilterModule.setDateTo(filterValues[FILTER_ITEMS.DATE_TO])
      analyticsFilterModule.setRequestOutcomes(filterValues[FILTER_ITEMS.REQUEST_OUTCOME])
      analyticsFilterModule.setAssistanceResolutions(filterValues[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS])
      analyticsFilterModule.setAnswerId(filterValues[FILTER_ITEMS.ANSWER_ID])
      analyticsFilterModule.setChannels(filterValues[FILTER_ITEMS.CHANNELS])

      if (this.$refs.conversationList) {
        this.$refs.conversationList.wasScrolled = false
      }

      this.cursor = {}
      this.changeBot(this.bot)
      // to reset contactId
      this.selectConversation(null, true)
      await this.loadInitialConversations()
    },

    selectConversation(contactId = null, calledFromMethod = false) {
      if (!calledFromMethod) this.onClearBubble()
      this.contactId = contactId
      const conversationDetailRef = this.$refs.conversationDetail
      if (contactId && conversationDetailRef) {
        conversationDetailRef.$el.scrollTop = 0
      }

      if (contactId === this.$route.query.contactId) return

      this.$router.replace({ query: { ...this.$route.query, contactId } })
    },

    async changeBot(bot) {
      if (this.botId === bot.id) return

      botModule.setBot(bot)
      this.$router.push({ name: ROUTE_NAMES.history, params: { botId: bot.id }, query: this.$route.query })
      await this.retrieveChannelList(bot.id)
    },

    async initFilters(botId = this.botId) {
      try {
        await Promise.all([
          analyticsFilterModule.setBot(botId),
          botModule.setBotList(),
          channelModule.setChannelList(botId)
        ])
        botModule.setBot(this.bot)
      } catch (err) {
        toaster.error(err)
      }
      this.setLoader('filters', false)
    },

    changeSuggestedAnswer(e) {
      this.formData.intentSuggestion.suggestedIntentPlatformId = e.suggestedIntentPlatformId
      this.formData.intentSuggestion.name = e.suggestedAnswerName
    },

    setLoader(loader, isLoading) {
      this.$set(this.loaders, loader, isLoading)
    },

    onRemoteActionSelected(formData) {
      this.formData = formData
      this.selectedBubble = {
        messageId: this.formData.id,
        requestData: this.formData.data.remoteAction,
        responseData: this.formData.data.remoteResponse || this.formData.data.context.apiCall
      }
    },

    onChatGPTActionSelected(formData) {
      const resolvedFormData = clone(formData)
      if (formData.data.action.prompt.startsWith('1. If the user is asking for product/service recommendations')) {
        if (!this.showUnreleased) resolvedFormData.data.action.prompt = '**AMIO PROMPT HIDDEN**'
        resolvedFormData.data.action.isFallback = true
      }
      this.formData = resolvedFormData
      this.selectedBubble = {
        messageId: this.formData.id
      }
    },

    onEventSelected(formData) {
      this.formData = formData
      this.selectedBubble = {
        messageId: this.formData.id
      }
    },

    onClearBubble() {
      this.formData = {}
      this.selectedBubble = null
    }
  },

  async mounted() {
    this.setLoader('conversations', true)
    this.setLoader('conversationDetail', true)
    await this.initFilters()
    await Promise.all([
      this.loadAnswers(),
      variablesModule.fetchVariables()
    ])
    await this.loadInitialConversations()
    this.initialLoading = false
  },

  watch: {
    botId: {
      immediate: false,
      handler() {
        this.loadAnswers()
      }
    }
  }
}
</script>

<style lang="scss">
.conversations {
  .slide-fade-enter-active {
    transition: all .3s ease;
  }

  .slide-fade-leave-active {
    transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateX(-10px);
    opacity: 0;
  }
}
</style>
